import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { MUSIC24_MONTHS_PER_YEARLY_PRICE } from 'fym-shared/src/music24/music24Plans';
import { offerSelectedTier, type UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { useMusic24Checkout } from '../../components/SMLPricing/hooks/useMusic24Checkout';
import { SMLUpgradePlanOptions } from '../../components/SMLUpgradeModal/SMLUpgradePlanOptions';
import { upgradeMessages } from '../../components/SMLUpgradeModal/upgradeMessages';
import { planNameMessages } from '../../config/music24PlanNames';
import { BillingInterval, MUSIC24_GENERAL_PLANS, Music24PlanId } from '../../config/music24StripePlans';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { useWatermarkPlanOptions, WATERMARK_PLAN_OPTIONS } from '../offer/useWatermarkPlanOptions';
import { WatermarkGets } from '../offer/WatermarkGets';
import { watermarkOfferMessages } from '../offer/watermarkOfferMessages';
import { watermarkTrialMessages as messages } from '../trial/watermarkTrialMessages';
import { useUpsellEventFacts } from '../useUpsellEventFacts';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSummary } from '../useWatermarkSummary';
import { trackUpsellCheckoutStarted, trackUpsellClicked, trackUpsellDismissed } from '../watermarkUpsellAnalytics';
import { useNeverHadTrial } from '../trial/useNeverHadTrial';
import { TrialTakeoverFiles } from './TrialTakeoverFiles';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TrialTakeoverProps {
  kind: 'lastDay' | 'free';
  onClose: () => void;
}

const SURFACES: Record<TrialTakeoverProps['kind'], UpsellSurface> = {
  lastDay: 'takeover_last_day',
  free: 'takeover_free',
};

/**
 * The takeover of the last trial day and of the first visit on Free (design: trial-upsell b3), one
 * time each, only for an owner with 1 or more watermarked files (`useUpsellAutoModal`). Artist is
 * selected, or Pro when a file needs it. The main button starts the Stripe checkout directly.
 */
const TrialTakeoverComponent: React.FC<TrialTakeoverProps> = ({ kind, onClose }) => {
  const intl = useIntl();
  const surface = SURFACES[kind];
  const { summary } = useWatermarkSummary();
  const neverHadTrial = useNeverHadTrial();
  const eventFacts = useUpsellEventFacts();
  const checkout = useMusic24Checkout();
  const plans = useMemo(() => MUSIC24_GENERAL_PLANS.slice(0, WATERMARK_PLAN_OPTIONS), []);
  const [billing, setBilling] = useState<BillingInterval>('monthly');
  const [pickedId, setPickedId] = useState<Music24PlanId | null>(null);
  const selectedId = pickedId ?? offerSelectedTier(summary);
  const options = useWatermarkPlanOptions({ plans, selectedPlanId: selectedId, interval: billing });
  const prefix = intl.formatMessage(messages.planPrefix);
  const prefixed = useMemo(
    () => options.map((option) => ({ ...option, detail: `${prefix} ${option.detail}` })),
    [options, prefix]
  );
  useUpsellShown(surface, true);

  const dismiss = useCallback(() => {
    trackUpsellDismissed(eventFacts(surface));
    onClose();
  }, [eventFacts, onClose, surface]);
  const { subscribe } = checkout;
  const start = useCallback(() => {
    const plan = plans.find((one) => one.id === selectedId);
    if (plan === undefined) {
      return;
    }
    trackUpsellClicked(eventFacts(surface));
    trackUpsellCheckoutStarted({ ...eventFacts(surface), tier: plan.id, interval: billing });
    subscribe(plan, billing, { upsellSurface: surface }).catch(console.error);
  }, [billing, eventFacts, plans, selectedId, subscribe, surface]);
  const isBusy = checkout.loadingPlanId !== null;
  // The count is a promise for the files made already: only while the licence can get its run (spec §14.1).
  let cta = intl.formatMessage(watermarkChoiceMessages.remove);
  if (isBusy) {
    cta = intl.formatMessage(upgradeMessages.upgradeCtaBusy);
  } else if (summary.cleanCopiesAvailable) {
    cta = intl.formatMessage(messages.takeoverCta, { count: summary.files });
  }

  return (
    <Modal
      isOpen
      toggle={dismiss}
      centered
      scrollable
      className={clsx(styles.dialog, styles.dialogWide)}
      contentClassName={styles.content}
      data-testid={`trial-takeover-${kind}`}
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.close}
          onClick={dismiss}
          aria-label={intl.formatMessage(watermarkChoiceMessages.close)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className={styles.two}>
          <TrialTakeoverFiles kind={kind} summary={summary} neverHadTrial={neverHadTrial} />
          <div className={styles.col}>
            <div className={styles.seg}>
              <button
                type="button"
                className={clsx(billing === 'monthly' && styles.segOn)}
                onClick={() => setBilling('monthly')}
              >
                {intl.formatMessage(messages.monthly)}
              </button>
              <button
                type="button"
                className={clsx(billing === 'yearly' && styles.segOn)}
                onClick={() => setBilling('yearly')}
              >
                {intl.formatMessage(messages.yearly, { months: 12 - MUSIC24_MONTHS_PER_YEARLY_PRICE })}
              </button>
            </div>
            <SMLUpgradePlanOptions options={prefixed} onSelect={setPickedId} />
            {summary.needsPro > 0 && (
              <p className={styles.fine}>
                {intl.formatMessage(watermarkOfferMessages.needsPro, {
                  plan: intl.formatMessage(planNameMessages.pro),
                })}
              </p>
            )}
            <WatermarkGets summary={summary} variant="takeover" />
            <SMLButton variant="primary" className={styles.stretch} disabled={isBusy} onClick={start}>
              {cta}
            </SMLButton>
            <button type="button" className={clsx(styles.later, styles.laterCenter)} onClick={dismiss}>
              {intl.formatMessage(kind === 'lastDay' ? messages.takeoverDismissLastDay : messages.takeoverDismissFree)}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const TrialTakeover = TypedMemo(TrialTakeoverComponent);
